import { Entry } from 'contentful';
import Image from 'next/image';
import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { BREAKPOINTS } from '@/core/constants';

interface SocialProofProps {
  entryTranslations: Entry<Record<string, any>>;
}

type KeyListValues = {
  id: string;
  key: string;
  value: string;
};

interface TranslationObject {
  [key: string]: string;
}

interface EditorialCardProps {
  title: string;
  asset: string;
  description: string;
  breakAfter?: boolean;
}

type AvatarProps = {
  asset: string;
  title: string;
  handle: string;
};

interface SocialCardProps {
  asset: string;
  description: string;
  avatar: AvatarProps;
  breakAfter?: boolean;
}

const buildSocialProofEntries = (keyValues: Record<string, any>) => {
  const translationObject: TranslationObject = {};

  keyValues.forEach((item: KeyListValues) => {
    const { key, value } = item;
    translationObject[key] = value;
  });

  return {
    title: translationObject.title,
    subtitle: translationObject.subtitle,
    starsImage: translationObject.starsImage,
    reviews: translationObject.reviews,
    reviewsLink: translationObject.reviewsLink,

    block_1: {
      asset: translationObject.block_1_asset,
    },
    block_2: {
      title: translationObject.block_2_title,
      asset: translationObject.block_2_asset,
      description: translationObject.block_2_description_1,
    },
    block_3: {
      asset: translationObject.block_3_asset,
      description: translationObject.block_3_description_1,
      avatar: {
        asset: translationObject.block_3_avatar_asset,
        title: translationObject.block_3_avatar_title,
        handle: translationObject.block_3_avatar_handle,
      },
    },
    block_4: {
      description: translationObject.block_4_description_1,
      avatar: {
        asset: translationObject.block_4_avatar_asset,
        title: translationObject.block_4_avatar_title,
        handle: translationObject.block_4_avatar_handle,
      },
    },
    block_5: {
      asset: translationObject.block_5_asset,
      avatar: {
        asset: translationObject.block_5_avatar_asset,
        title: translationObject.block_5_avatar_title,
        handle: translationObject.block_5_avatar_handle,
      },
    },
    block_6: {
      asset: translationObject.block_6_asset,
      title: translationObject.block_6_title,
      description: translationObject.block_6_description_1,
    },
    block_7: {
      asset: translationObject.block_7_asset,
      description: translationObject.block_7_description_1,
      avatar: {
        asset: translationObject.block_7_avatar_asset,
        title: translationObject.block_7_avatar_title,
        handle: translationObject.block_7_avatar_handle,
      },
    },
  };
};

const ReviewStars = ({ count, src }: { count: number; src: string }) => {
  return (
    <Image
      className="h-[25px] w-auto"
      src={src}
      width={200}
      height={25}
      alt="Lovevery"
    />
  );
};

const Card = ({
  children,
  classes,
}: {
  children: React.ReactNode;
  classes?: string;
}) => {
  return (
    <div
      className={classNames(
        'rounded-lg shadow-2xl mb-4 lg:mb-8 bg-white',
        classes,
      )}
    >
      {children}
    </div>
  );
};

const VideoBlock = ({
  src,
  ref,
}: {
  src: string;
  ref?: RefObject<HTMLVideoElement> | RefObject<HTMLImageElement>;
}) => {
  if (!src) return null;

  return (
    <Card>
      <video
        ref={ref as RefObject<HTMLVideoElement>}
        width="w-full"
        className="rounded-lg"
        height="400"
        controls
        playsInline // to play video on iOS
        autoPlay
        muted
        loop
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Card>
  );
};

const EditorialCard: FC<EditorialCardProps> = ({
  title,
  asset,
  description,
  breakAfter,
}) => {
  return (
    <Card
      classes={`p-4 md:p-[2vw] flex flex-col ${breakAfter ? 'break-after-column' : ''}`}
    >
      <div className="flex flex-row mb-4 items-end justify-between">
        <h3 className="text-[13px] md:text-[16px] leading-5 font-bold">
          {title}
        </h3>
        <Image
          className="w-1/3 max-w-[45px] h-full md:max-w-none"
          src={asset}
          width={100}
          height={50}
          alt={title}
        />
      </div>

      <p className="text-[13px] leading-5 md:text-base">{description}</p>
    </Card>
  );
};

const SocialAvatar = ({ avatar }: { avatar: AvatarProps }) => {
  return (
    <div className="flex items-center gap-2 md:py-4">
      <Image
        className="max-w-[40px] max-h-[40px] md:max-w-[50px] md:max-h-[50px]"
        src={avatar.asset}
        width={50}
        height={50}
        alt={avatar.handle}
      />
      <div className="flex flex-col justify-center">
        <p className="text-[13px] font-bold leading-5">{avatar.title}</p>
        <p className="text-[13px] leading-5">{avatar.handle}</p>
      </div>
    </div>
  );
};

const SocialCard: FC<SocialCardProps> = ({
  avatar,
  asset,
  description,
  breakAfter,
}) => {
  return (
    <Card classes={breakAfter ? 'break-after-column' : ''}>
      {asset && (
        <Image
          className="rounded-t-lg"
          src={asset}
          width={400}
          height={400}
          alt={avatar.title}
        />
      )}
      <div className="py-2 px-4 md:p-[2vw] ">
        {description && (
          <p className="text-[13px] mb-2 leading-5 md:text-base">
            {description}
          </p>
        )}
        {avatar && <SocialAvatar avatar={avatar} />}
      </div>
    </Card>
  );
};

const SocialProof: FC<
  SocialProofProps & {
    isVisible: boolean;
  }
> = ({ entryTranslations, isVisible = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (entryTranslations) {
      const { keyValues } = entryTranslations?.fields;

      if (keyValues) {
        const socialProofEntries = buildSocialProofEntries(keyValues);
        setTranslations(socialProofEntries);
      }
    }
  }, [entryTranslations, setTranslations]);

  useEffect(() => {
    if (isVisible) {
      videoRef.current && videoRef.current.play();
    } else {
      videoRef.current && videoRef.current.pause();
    }
  }, [isVisible]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.md);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!translations) return null;

  return (
    <section
      data-testid={`social-proof`}
      className="bg-blue-tint pt-8 pb-12 lg:pt-20 lg:pb-16"
    >
      <div className="flex flex-col gap-4 px-4">
        <h2 className="text-[32px] md:text-[45px] font-bold text-center leading-10">
          {translations.title}
        </h2>
        <h3 className="text-xl md:text-2xl font-bold text-center leading-8">
          {translations.subtitle}
        </h3>
        <div className="flex gap-2 justify-center items-center">
          <ReviewStars count={5} src={translations.starsImage} />
          <p className="text-[18px] md:text-xl text-indigo">
            {translations.reviews}
          </p>
        </div>
      </div>
      <div className="mx-auto mt-8 px-2 max-w-[1400px] ">
        {isMobile ? (
          <div className="gap-4 lg:gap-8 columns-2 md:columns-4">
            <VideoBlock src={translations?.block_1?.asset} ref={videoRef} />
            <EditorialCard {...translations?.block_6} />
            <SocialCard {...translations?.block_7} breakAfter={true} />
            <EditorialCard {...translations?.block_2} />
            <SocialCard {...translations?.block_3} />
            <SocialCard {...translations?.block_5} />
          </div>
        ) : (
          <div className="gap-4 lg:gap-8 columns-2 md:columns-4">
            <VideoBlock src={translations?.block_1?.asset} ref={videoRef} />
            <EditorialCard {...translations?.block_2} breakAfter={true} />
            <SocialCard {...translations?.block_3} />
            <SocialCard {...translations?.block_4} breakAfter={true} />
            <SocialCard {...translations?.block_5} breakAfter={true} />
            <EditorialCard {...translations?.block_6} />
            <SocialCard {...translations?.block_7} />
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialProof;
